import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, ChevronDown, ChevronUp, ChevronRight, Bookmark, Edit3, AlertCircle, FileText } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { MedicalSession } from "@/types/medical-session";
import type { SavedQuestion } from "@/types/saved-question";

interface CareTeamQuestionsProps {
  sessionId: string;
  patientId: string;
}

export function CareTeamQuestions({ sessionId, patientId: _patientId }: CareTeamQuestionsProps) {
  const { t } = useTranslation();
  const [newQuestion, setNewQuestion] = useState("");
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [expandedAiAnswers, setExpandedAiAnswers] = useState<Set<string>>(new Set());
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editedQuestionText, setEditedQuestionText] = useState("");
  const [showGeneralNotes, setShowGeneralNotes] = useState(false);
  const [generalNotes, setGeneralNotes] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch medical session to get general notes
  const { data: medicalSession } = useQuery<MedicalSession>({
    queryKey: [`/api/medical-session-by-id/${sessionId}`],
    queryFn: () => api.getMedicalSession(sessionId),
    enabled: !!sessionId,
  });

  // Fetch saved questions from API
  const { data: savedQuestions = [], isLoading } = useQuery<SavedQuestion[]>({
    queryKey: ["/api/saved-questions", sessionId],
    queryFn: () => api.getSavedQuestions(sessionId),
  });

  // Sync general notes from medical session when it loads
  useEffect(() => {
    if (medicalSession) {
      console.log('[General Notes] Loaded from session:', {
        sessionId,
        notesLength: medicalSession.generalNotes?.length,
        notes: medicalSession.generalNotes
      });
      setGeneralNotes(medicalSession.generalNotes ?? '');
    }
  }, [medicalSession, sessionId]);

  // Update general notes mutation with debouncing
  const updateNotesMutation = useMutation({
    mutationFn: (notes: string) => {
      console.log('[General Notes] Mutation called with:', { notesLength: notes.length });
      return apiRequest("PATCH", `/api/medical-session/${sessionId}/notes`, { generalNotes: notes });
    },
    onSuccess: (data) => {
      console.log('[General Notes] Save successful:', data);
      queryClient.invalidateQueries({ queryKey: [`/api/medical-session-by-id/${sessionId}`] });
    },
    onError: (error) => {
      console.error('[General Notes] Save failed:', error);
    }
  });

  // Debounced save for general notes
  useEffect(() => {
    if (!medicalSession) return; // Don't save until session is loaded

    const timeoutId = setTimeout(() => {
      if (generalNotes !== medicalSession.generalNotes) {
        console.log('[General Notes] Saving notes to database:', {
          sessionId,
          notesLength: generalNotes.length
        });
        updateNotesMutation.mutate(generalNotes);
      }
    }, 1000); // Debounce: save 1 second after user stops typing

    return () => clearTimeout(timeoutId);
  }, [generalNotes, medicalSession, sessionId, updateNotesMutation]);

  // Add question mutation
  const addQuestionMutation = useMutation({
    mutationFn: (data: { question: string }) =>
      apiRequest("POST", "/api/saved-questions", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/saved-questions", sessionId] });
      setNewQuestion("");
      toast({
        title: t('records.questionAddedTitle'),
        description: t('records.questionAddedDescription'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common.errorTitle'),
        description: error instanceof Error ? error.message : t('records.addQuestionFailed'),
        variant: "destructive",
      });
    },
  });

  // Update question mutation with optimistic updates
  const updateQuestionMutation = useMutation({
    mutationFn: ({ questionId, updates }: { questionId: string; updates: Partial<SavedQuestion> }) =>
      apiRequest("PATCH", `/api/saved-questions/${questionId}`, updates),
    onMutate: async ({ questionId, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/saved-questions", sessionId] });

      // Snapshot previous value
      const previousQuestions = queryClient.getQueryData<SavedQuestion[]>(["/api/saved-questions", sessionId]);

      // Optimistically update cache
      queryClient.setQueryData<SavedQuestion[]>(
        ["/api/saved-questions", sessionId],
        (old = []) => old.map(q => q.id === questionId ? { ...q, ...updates } : q)
      );

      return { previousQuestions };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousQuestions) {
        queryClient.setQueryData(["/api/saved-questions", sessionId], context.previousQuestions);
      }
      toast({
        title: t('common.errorTitle'),
        description: error instanceof Error ? error.message : t('records.updateQuestionFailed'),
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["/api/saved-questions", sessionId] });
    },
  });

  // Delete question mutation with optimistic updates
  const deleteQuestionMutation = useMutation({
    mutationFn: (questionId: string) =>
      apiRequest("DELETE", `/api/saved-questions/${questionId}`),
    onMutate: async (questionId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/saved-questions", sessionId] });

      // Snapshot previous value
      const previousQuestions = queryClient.getQueryData<SavedQuestion[]>(["/api/saved-questions", sessionId]);

      // Optimistically remove from cache
      queryClient.setQueryData<SavedQuestion[]>(
        ["/api/saved-questions", sessionId],
        (old = []) => old.filter(q => q.id !== questionId)
      );

      return { previousQuestions };
    },
    onSuccess: () => {
      toast({
        title: t('records.questionDeletedTitle'),
        description: t('records.questionDeletedDescription'),
      });
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousQuestions) {
        queryClient.setQueryData(["/api/saved-questions", sessionId], context.previousQuestions);
      }
      toast({
        title: t('common.errorTitle'),
        description: error instanceof Error ? error.message : t('records.deleteQuestionFailed'),
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["/api/saved-questions", sessionId] });
    },
  });

  const handleAddQuestion = () => {
    const trimmedQuestion = newQuestion.trim();
    if (!trimmedQuestion) return;

    addQuestionMutation.mutate({
      question: trimmedQuestion,
    });
  };

  const handleToggleAsked = (questionId: string, currentStatus: boolean) => {
    updateQuestionMutation.mutate({
      questionId,
      updates: { isAsked: !currentStatus },
    });
  };

  const handleUpdateNotes = (questionId: string, notes: string) => {
    updateQuestionMutation.mutate({
      questionId,
      updates: { myNotes: notes },
    });
  };

  const handleTogglePriority = (questionId: string, currentPriority: string) => {
    const newPriority = currentPriority === "high" ? "normal" : "high";
    updateQuestionMutation.mutate({
      questionId,
      updates: { priority: newPriority },
    });
  };

  const handleDeleteQuestion = (questionId: string) => {
    deleteQuestionMutation.mutate(questionId);
  };

  const handleClearAskedQuestions = () => {
    const askedQuestions = savedQuestions.filter(q => q.isAsked);
    askedQuestions.forEach(q => {
      deleteQuestionMutation.mutate(q.id);
    });
  };

  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
        // Default AI answer to expanded when question is first expanded
        setExpandedAiAnswers(aiPrev => {
          const newAiSet = new Set(aiPrev);
          newAiSet.add(questionId);
          return newAiSet;
        });
      }
      return newSet;
    });
  };

  const toggleAiAnswer = (questionId: string) => {
    setExpandedAiAnswers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const startEditingQuestion = (questionId: string, currentText: string) => {
    setEditingQuestionId(questionId);
    setEditedQuestionText(currentText);
  };

  const saveEditedQuestion = (questionId: string) => {
    if (editedQuestionText.trim() && editedQuestionText !== savedQuestions.find(q => q.id === questionId)?.question) {
      updateQuestionMutation.mutate({
        questionId,
        updates: { question: editedQuestionText.trim() },
      });
    }
    setEditingQuestionId(null);
    setEditedQuestionText("");
  };

  const cancelEditingQuestion = () => {
    setEditingQuestionId(null);
    setEditedQuestionText("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddQuestion();
    }
  };

  const handleQuestionEditKeyPress = (e: React.KeyboardEvent, questionId: string) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      saveEditedQuestion(questionId);
    } else if (e.key === "Escape") {
      cancelEditingQuestion();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* General Notes Section */}
      <div className="border border-gray-200 rounded-lg bg-white">
        <button
          onClick={() => setShowGeneralNotes(!showGeneralNotes)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
          aria-expanded={showGeneralNotes}
          aria-label={showGeneralNotes ? t('records.collapseGeneralNotes') : t('records.expandGeneralNotes')}
          data-testid="button-toggle-general-notes"
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-gray-700">{t('records.generalNotes')}</span>
            <span className="text-xs text-gray-500">{t('records.generalNotesHint')}</span>
          </div>
          {showGeneralNotes ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
        </button>
        {showGeneralNotes && (
          <div className="p-3 pt-0">
            <Textarea
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
              placeholder={t('records.generalNotesPlaceholder')}
              className="text-sm min-h-[100px] resize-y"
              data-testid="textarea-general-notes"
            />
          </div>
        )}
      </div>

      {/* Add Manual Question */}
      <div className="pb-4 border-b border-gray-200">
        <div className="flex space-x-2">
          <Input
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('records.addYourOwnQuestion')}
            className="flex-1"
            data-testid="input-new-question"
          />
          <Button
            onClick={handleAddQuestion}
            disabled={!newQuestion.trim() || addQuestionMutation.isPending}
            className="bg-primary hover:bg-primary-dark"
            data-testid="button-add-question"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('records.add')}
          </Button>
        </div>
        <p className="text-xs text-text-secondary mt-2">
          {t('records.bookmarkHint')}
        </p>
      </div>

      {/* Consolidated Questions List */}
      {savedQuestions.length > 0 && (
        <div className="space-y-2">
          {[...savedQuestions]
            .sort((a, b) => {
              // Sorting priority: urgent first, crossed out last, normal in middle
              // Within each group, sort chronologically (most recent first)

              const aIsUrgent = a.priority === "high" && !a.isAsked;
              const bIsUrgent = b.priority === "high" && !b.isAsked;
              const aIsCrossedOut = a.isAsked;
              const bIsCrossedOut = b.isAsked;

              // Urgent questions go to top
              if (aIsUrgent && !bIsUrgent) return -1;
              if (!aIsUrgent && bIsUrgent) return 1;

              // Crossed out questions go to bottom
              if (aIsCrossedOut && !bIsCrossedOut) return 1;
              if (!aIsCrossedOut && bIsCrossedOut) return -1;

              // Within same group, sort by creation time (most recent first)
              const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
              return bTime - aTime;
            })
            .map((question) => {
            const isExpanded = expandedQuestions.has(question.id);
            const isAiAnswerExpanded = expandedAiAnswers.has(question.id);
            const isFromChat = question.aiContext !== null && question.aiContext !== undefined;
            const isEditing = editingQuestionId === question.id;
            const isHighPriority = question.priority === "high";
            
            return (
              <div
                key={question.id}
                className={`group/row transition-all ${isExpanded ? 'bg-gray-50 rounded-lg p-3 border border-gray-200' : 'hover:bg-gray-50 py-2 px-1 rounded'}`}
                data-testid={`question-${question.id}`}
              >
                {/* Collapsed: Simple row */}
                <div className="flex items-center gap-2">
                  {/* Expand arrow */}
                  <button
                    onClick={() => toggleQuestion(question.id)}
                    className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded transition-colors opacity-60 hover:opacity-100"
                    title={isExpanded ? t('records.collapse') : t('records.expand')}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? t('records.collapseQuestionDetails') : t('records.expandQuestionDetails')}
                    data-testid={`button-toggle-expand-${question.id}`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5 text-gray-600" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
                    )}
                  </button>
                  
                  {/* Question text */}
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div>
                        <Input
                          value={editedQuestionText}
                          onChange={(e) => setEditedQuestionText(e.target.value)}
                          onKeyDown={(e) => handleQuestionEditKeyPress(e, question.id)}
                          onBlur={() => saveEditedQuestion(question.id)}
                          className="text-sm"
                          autoFocus
                          data-testid={`input-edit-question-${question.id}`}
                        />
                        <p className="text-xs text-text-secondary mt-1">{t('records.pressEnterToSave')}</p>
                      </div>
                    ) : (
                      <button
                        onClick={() => toggleQuestion(question.id)}
                        className={`text-left text-sm w-full ${
                          question.isAsked ? 'line-through text-gray-400' : 'text-gray-900'
                        }`}
                      >
                        {question.question}
                      </button>
                    )}
                    
                    {/* Small metadata - only if not expanded */}
                    {!isExpanded && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-xs ${isFromChat ? 'text-blue-600' : 'text-gray-500'}`}>
                          {isFromChat ? t('records.fromChat') : t('records.manual')}
                        </span>
                        {isHighPriority && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="text-xs text-red-600 font-medium">{t('records.urgent')}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Right side: checkbox and icons */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => startEditingQuestion(question.id, question.question)}
                      className="opacity-0 group-hover/row:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded"
                      title={t('records.edit')}
                      aria-label={t('records.editQuestion')}
                      data-testid={`button-edit-question-${question.id}`}
                    >
                      <Edit3 className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                    <div title={question.isAsked ? t('records.markAsNotAsked') : t('records.markAsAsked')}>
                      <Checkbox
                        id={`question-${question.id}`}
                        checked={question.isAsked}
                        onCheckedChange={() => handleToggleAsked(question.id, question.isAsked)}
                        aria-label={question.isAsked ? t('records.markAsNotAsked') : t('records.markAsAsked')}
                        data-testid={`checkbox-asked-${question.id}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600 opacity-0 group-hover/row:opacity-100 transition-opacity"
                      data-testid={`button-delete-${question.id}`}
                      aria-label={t('records.deleteQuestion')}
                      title={t('records.delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                
                {/* Expanded content */}
                {isExpanded && (
                  <div className="ml-6 mt-3 pt-3 border-t border-gray-200">
                    <div className="space-y-3">
                      {/* Actions bar */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleTogglePriority(question.id, question.priority)}
                          className={`text-xs px-2.5 py-1.5 rounded flex items-center gap-1.5 transition-colors border font-medium ${
                            isHighPriority
                              ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                          }`}
                          title={isHighPriority ? t('records.markAsNormalPriority') : t('records.markAsUrgent')}
                          data-testid={`button-toggle-priority-${question.id}`}
                        >
                          <AlertCircle className="h-3.5 w-3.5" />
                          {isHighPriority ? t('records.removeUrgent') : t('records.markAsUrgent')}
                        </button>
                      </div>

                      {/* LitMed Answer - collapsible, expanded by default */}
                      {isFromChat && question.aiContext && (
                        <div className="bg-blue-50 rounded-lg border border-blue-200 overflow-hidden">
                          <button
                            onClick={() => toggleAiAnswer(question.id)}
                            className="w-full flex items-center justify-between p-3 hover:bg-blue-100 transition-colors"
                            aria-expanded={isAiAnswerExpanded}
                            aria-label={isAiAnswerExpanded ? t('records.collapseAiAnswer') : t('records.expandAiAnswer')}
                            data-testid={`button-toggle-ai-answer-${question.id}`}
                          >
                            <p className="text-xs font-semibold text-blue-900">{t('records.answerFromLitMed')}</p>
                            {isAiAnswerExpanded ? (
                              <ChevronUp className="h-3.5 w-3.5 text-blue-700" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5 text-blue-700" />
                            )}
                          </button>
                          {isAiAnswerExpanded && (
                            <div className="px-3 pb-3">
                              <div className="prose prose-sm max-w-none text-xs text-gray-700">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {question.aiContext}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Combined Notes Section */}
                      <div>
                        <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                          {t('records.notesLabel')}
                        </label>
                        <Textarea
                          value={question.myNotes || ""}
                          onChange={(e) => handleUpdateNotes(question.id, e.target.value)}
                          placeholder={t('records.questionNotesPlaceholder')}
                          className="text-sm min-h-[80px] resize-y border-gray-200"
                          data-testid={`textarea-notes-${question.id}`}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {savedQuestions.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          <Bookmark className="h-12 w-12 mx-auto mb-4 opacity-40" />
          <p className="text-sm font-medium mb-1">{t('records.noQuestionsSavedYet')}</p>
          <p className="text-xs">
            {t('records.noQuestionsSavedHint')}
          </p>
        </div>
      )}

      {/* Clear Asked Questions Button */}
      {savedQuestions.some(q => q.isAsked) && (
        <div className="pt-4 border-t border-gray-200">
          <Button
            onClick={handleClearAskedQuestions}
            variant="outline"
            className="w-full text-gray-600 hover:text-red-600 hover:border-red-300 hover:bg-red-50"
            data-testid="button-clear-asked-questions"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('records.clearAskedQuestions', { count: savedQuestions.filter(q => q.isAsked).length })}
          </Button>
        </div>
      )}
    </div>
  );
}
